import React, { useState, useEffect } from "react";
import { adminApi } from "../services/api";
import { LineChart, Line, BarChart, Bar, PieChart, Pie, Cell, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { formatPercentage } from "../utils";

const DashboardPage = () => {
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Graph data states
  const [userGrowthPeriod, setUserGrowthPeriod] = useState("monthly");
  const [userGrowthData, setUserGrowthData] = useState([]);

  const [apiUsagePeriod, setApiUsagePeriod] = useState("monthly");
  const [apiUsageData, setApiUsageData] = useState([]);

  const [apiCallsPeriod, setApiCallsPeriod] = useState("monthly");
  const [apiCallsData, setApiCallsData] = useState([]);

  const [errorSuccessRatioPeriod, setErrorSuccessRatioPeriod] = useState("monthly");
  const [errorSuccessRatioData, setErrorSuccessRatioData] = useState([]);

  useEffect(() => {
    fetchOverview();
  }, []);

  useEffect(() => {
    fetchUserGrowth();
  }, [userGrowthPeriod]);

  useEffect(() => {
    fetchApiUsage();
  }, [apiUsagePeriod]);

  useEffect(() => {
    fetchApiCalls();
  }, [apiCallsPeriod]);

  useEffect(() => {
    fetchErrorSuccessRatio();
  }, [errorSuccessRatioPeriod]);

  const fetchOverview = async () => {
    try {
      const response = await adminApi.getAnalyticsOverview();
      setOverview(response.data.overview);
    } catch (err) {
      console.error("Failed to fetch overview:", err);
      setError("Failed to load analytics data");
    } finally {
      setLoading(false);
    }
  };

  const fetchUserGrowth = async () => {
    try {
      const response = await adminApi.getUserGrowth(userGrowthPeriod);
      setUserGrowthData(response.data.data);
    } catch (err) {
      console.error("Failed to fetch user growth:", err);
    }
  };

  const fetchApiUsage = async () => {
    try {
      const response = await adminApi.getApiUsage(apiUsagePeriod);
      setApiUsageData(response.data.data);
    } catch (err) {
      console.error("Failed to fetch API usage:", err);
    }
  };

  const fetchApiCalls = async () => {
    try {
      const response = await adminApi.getApiCalls(apiCallsPeriod);
      setApiCallsData(response.data.data);
    } catch (err) {
      console.error("Failed to fetch API calls:", err);
    }
  };

  const fetchErrorSuccessRatio = async () => {
    try {
      const response = await adminApi.getApiUsage(errorSuccessRatioPeriod);
      setErrorSuccessRatioData(response.data.data);
    } catch (err) {
      console.error("Failed to fetch error/success ratio:", err);
    }
  };

  const PeriodSelector = ({ value, onChange }) => (
    <div className="flex border border-line rounded-lg overflow-hidden bg-white">
      {["weekly", "monthly", "yearly"].map((period) => (
        <button
          key={period}
          onClick={() => onChange(period)}
          className={`border-0 py-2 px-4 text-sm font-medium cursor-pointer border-r border-line last:border-r-0 transition-all ${
            value === period ? "bg-[#1e293b] text-white" : "text-muted hover:bg-soft "
          }`}
        >
          {period.charAt(0).toUpperCase() + period.slice(1)}
        </button>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="px-6 py-8 pb-24 font-sans text-ink bg-white h-full">
        <div className="py-16 text-center">
          <div className="inline-block w-8 h-8 border-3 border-line border-t-indigo rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="px-6 py-8 pb-24 font-sans text-ink bg-white h-full">
        <div className="py-16 text-center text-red-600">{error}</div>
      </div>
    );
  }

  const getGreeting = () => {
    const hour = new Date().getHours();
    if (hour < 12) return "Good morning";
    if (hour < 17) return "Good afternoon";
    return "Good evening";
  };

  return (
    <div className="px-6 py-8 pb-24 font-sans text-ink bg-white h-full">
      <div className="mb-4">
        <h1 className="text-[28px] font-bold tracking-tight m-0">Dashboard</h1>
        <p className="text-muted text-[15px] mt-1 mb-0">{getGreeting()}, Admin! Monitor API usage, user growth, and system performance metrics.</p>
      </div>

      {/* State Cards */}
      <div className="grid grid-cols-5 gap-4 mb-6">
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Total Users</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight">{overview?.totalUsers || 0}</div>
        </div>
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Total API Calls</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight">{overview?.totalApiCalls || 0}</div>
        </div>
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Failed API Calls</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight text-red-700">{overview?.totalFailedApiCalls || 0}</div>
        </div>
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Error Rate</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight text-yellow-700">{formatPercentage(overview?.errorRate || 0)}%</div>
        </div>
        <div className="border border-line rounded-xl p-4 bg-white">
          <div className="text-xs text-muted uppercase tracking-wider font-semibold">Success Rate</div>
          <div className="text-[26px] font-bold mt-1 tracking-tight text-green-700">{formatPercentage(100 - (overview?.errorRate || 0))}%</div>
        </div>
      </div>

      {/* Graphs */}
      <div className="grid grid-cols-2 gap-6 mb-6">
        {/* Users Growth */}
        <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">Users Growth</h3>
            <PeriodSelector value={userGrowthPeriod} onChange={setUserGrowthPeriod} />
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={userGrowthData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dy={10} />
                <YAxis stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dx={-10} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    padding: "8px 12px",
                  }}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#3b82f6"
                  strokeWidth={2.5}
                  dot={{ fill: "#3b82f6", strokeWidth: 0, r: 5 }}
                  activeDot={{ r: 7 }}
                  name="Users"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* API Usage */}
        <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">API Usage</h3>
            <PeriodSelector value={apiUsagePeriod} onChange={setApiUsagePeriod} />
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={apiUsageData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dy={10} />
                <YAxis stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dx={-10} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    padding: "8px 12px",
                  }}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />
                <Bar dataKey="successCount" stackId="a" fill="#10b981" name="Success" radius={[0, 0, 0, 0]} />
                <Bar dataKey="errorCount" stackId="a" fill="#ef4444" name="Error" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* API Calls */}
        <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">API Calls</h3>
            <PeriodSelector value={apiCallsPeriod} onChange={setApiCallsPeriod} />
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={apiCallsData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dy={10} />
                <YAxis stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dx={-10} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    padding: "8px 12px",
                  }}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />
                <Line
                  type="monotone"
                  dataKey="count"
                  stroke="#8b5cf6"
                  strokeWidth={2.5}
                  dot={{ fill: "#8b5cf6", strokeWidth: 0, r: 5 }}
                  activeDot={{ r: 7 }}
                  name="API Calls"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Error & Success Ratio Trend */}
        <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">Error & Success Ratio Trend</h3>
            <PeriodSelector value={errorSuccessRatioPeriod} onChange={setErrorSuccessRatioPeriod} />
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={errorSuccessRatioData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" vertical={false} />
                <XAxis dataKey="date" stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dy={10} />
                <YAxis stroke="#6b7280" fontSize={11} tickLine={false} axisLine={false} dx={-10} />
                <Tooltip
                  contentStyle={{
                    backgroundColor: "#fff",
                    border: "1px solid #e5e7eb",
                    borderRadius: "6px",
                    fontSize: "12px",
                    padding: "8px 12px",
                  }}
                />
                <Legend verticalAlign="bottom" height={36} iconType="circle" wrapperStyle={{ paddingTop: "20px" }} />
                <Line
                  type="monotone"
                  dataKey="successCount"
                  stroke="#10b981"
                  strokeWidth={2.5}
                  dot={{ fill: "#10b981", strokeWidth: 0, r: 5 }}
                  activeDot={{ r: 7 }}
                  name="Success"
                />
                <Line
                  type="monotone"
                  dataKey="errorCount"
                  stroke="#ef4444"
                  strokeWidth={2.5}
                  dot={{ fill: "#ef4444", strokeWidth: 0, r: 5 }}
                  activeDot={{ r: 7 }}
                  name="Error"
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Error & Success Ratio */}
        {/* <div className="border border-line rounded-2xl p-6 bg-white">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-bold text-ink m-0">Error & Success Ratio</h3>
          </div>
          <div className="h-[300px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={[
                    { name: 'Success', value: overview?.totalApiCalls - overview?.totalFailedApiCalls || 0 },
                    { name: 'Error', value: overview?.totalFailedApiCalls || 0 }
                  ]}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(1)}%`}
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  <Cell fill="#10b981" />
                  <Cell fill="#ef4444" />
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div> */}
      </div>
    </div>
  );
};

export default DashboardPage;
